//* /life/interviewOutcome/{applicantId}
//* applicantId: 1 or 2 indicating the first or second applicant in the quote

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "./queryKeys.js";
import useSharedData from "../useSharedData.ts";

//* TODO: MAP THE OUTCOME
import { interviewAuraOutcomeMapping } from "../../mapping/interviewAuraOutcomeMappingResponse.js";

//** Axios request */
import { request } from "../../utilities/axios-utils.js";

import { isObjEmpty } from "../../utilities/utils.js";

// import usePreQualification from "../../hooks/usePreQualification";

const MOCK_RESPONSE = {
  FULL_COVER_WITH_EXCLUSIONS: "fullCoverWithExclusions",
  FULL_COVER_NO_EXCLUSIONS: "fullCoverNoExclusions",
  CI_UP_SELL: "withCIUpSell",
  WITH_LIFE_AND_CI_LOADING: "withLifeAndCILoading",
  WITH_CLEAN_LIFE_AND_CI_LOADING: "withCleanLifeAndCILoading",
  PUW_DECLINE: "puwDecline",
  BOTH_DECLINE: "puwAndCiDecline",
  CI_DECLINE: "ciDeclinePuwAccept",
  LIFE_ONLY: "lifeOnly",
  LIFE_ONLY_COMPLETE: "lifeOnlyComplete",
  LIFE_ONLY_WITH_EXCLUSIONS: "lifeOnlyWithExclusions",
  WITH_LIFE_AND_PUW_LOADING: "withLifeAndPUWLoading",
  WITH_CLEAN_LIFE_AND_PUW_LOADING: "withCleanLifeAndPUWLoading",
  DECLINE: "decline",
  DECLINE_REFERRAL: "declineReferral",
  DECLINE_POSTPONE: "declinePostpone",
  ELIFE_TEST_ONE: "eLifeTestOne",
  NB_LIFE_AND_UPSELL: "nbLifeAndUpSell",
  NB_LIFE_EXCLUSIONS_AND_CI: "nbLifeExclusionsAndCI",
  NB_LIFE_CI_PUW_EXCLUSIONS: "nbLifeCAndPuwExclusions",
  ERROR_CI_WITH_TOTAL_LOADING: "errorCIWithTotalLoading",
};

//----------------------------------------------------------------

const getAuraOutcome = async (
  requestData,
  openErrorModal,
  isDevShared = false,
  queryClient
) => {
  try {
    const {
      accessToken,
      applicantId = 1, //* For phase One. We only have one applicant.
    } = { ...requestData };

    //! MOCK TEST ----------------------------------------------------------------
    //* TODO: isDev PRE-DEPLOY
    const isDev = isDevShared;

    if (isDev === true && import.meta.env.MODE === "development") {
      const originalDataOne = queryClient.getQueryData([
        queryKeys.ORIGINAL_AURA_OUTCOME,
      ]);

      if (!isObjEmpty(originalDataOne)) {
        return originalDataOne;
      }

      //* Get the mocked response data and map it
      import("../../data/mock/aura-interview-outcome-response-mock-data.js")
        .then((module) => {
          const interviewOutcomeResponse = module.interviewOutcomeResponse;
          const mockResponse = interviewAuraOutcomeMapping(
            interviewOutcomeResponse(MOCK_RESPONSE.ERROR_CI_WITH_TOTAL_LOADING)
          );

          queryClient.setQueryData(
            [queryKeys.ORIGINAL_AURA_OUTCOME],
            mockResponse
          );

          mockResponse.fromOriginal = false;
          return mockResponse;
        })
        .catch((error) => {
          console.log("Error loading mock data: ", error);
        });
    }
    //! MOCK TEST ----------------------------------------------------------------

    //* Check to see if we have the data in cache?
    //* if we do, return that as we can not make
    //* multiple calls to the interviewOutcome API
    const originalData = queryClient.getQueryData([
      queryKeys.ORIGINAL_AURA_OUTCOME,
    ]);

    //* return the cached response, if it exists.
    if (!isObjEmpty(originalData)) {
      const returnResponse = interviewAuraOutcomeMapping(originalData?.data);
      returnResponse.fromOriginal = true; //! TODO: THIS IS NOT BEING OVERWRITTEN HERE. WHY?
      return returnResponse;
    }

    const response = await request({
      url: `/interviewOutcome/${applicantId}`,
      method: "GET",
      accessToken: accessToken,
    });

    if (200 === response.status) {
      queryClient.setQueryData([queryKeys.ORIGINAL_AURA_OUTCOME], response);
      const returnResponse = interviewAuraOutcomeMapping(response?.data);
      returnResponse.fromOriginal = false;
      return returnResponse;
    } else {
      openErrorModal();
      throw response;
    }
  } catch (ex) {
    console.error(ex);
  }
};

export const useGetInterviewOutcome = (requestData) => {
  const { openErrorModal, isDevShared } = useSharedData();
  const queryClient = useQueryClient();

  let response = {};

  response = useQuery({
    queryKey: [queryKeys.GET_AURA_OUTCOME],
    queryFn: async () =>
      await getAuraOutcome(
        requestData,
        openErrorModal,
        isDevShared,
        queryClient
      ),
    refetchOnWindowFocus: false,
    enabled: !!requestData?.accessToken && requestData.enableGet === true,
    cacheTime: 10 * (60 * 1000),
  });

  return response;
};
